import type { FormEnforcements } from '@/models/form';

/** Why the New entry menu holds a form back. */
export type EntryRuleReason =
  /** Once per case, and the case already has an entry on it. */
  | 'onlyOnce'
  /** It enforces order, or waits behind a form that does on a case with no entries yet. */
  | 'order'
  /** The case does not meet the form's effects, by the per-case read's `allowEntry`. */
  | 'condition';

export interface EntryRule {
  allowed: boolean;
  /** Set when `allowed` is false. Where several apply, the one the menu shows. */
  reason?: EntryRuleReason;
  /** The listed entries include one on this form, which the menu marks as done. */
  hasEntry: boolean;
  /**
   * False where the case does not meet the form's effects, whatever `reason` says: the menu colours
   * the icon red for it even on a form that order holds back first.
   */
  conditionMet: boolean;
}

interface RuleForm {
  identifier: string;
  enforcements: FormEnforcements;
}

interface ListedEntry {
  formIdentifier: string;
}

// A form with no entry rule on and no place in the form order was never held back by the menu,
// whatever its `allowEntry` said, and its entries were not marked. Kept as the menu had it. A shared
// form this study never placed has a `null` place, which counts as none.
const hasRules = ({ enforceOrder, hidden, onlyOnce, order }: FormEnforcements) =>
  enforceOrder || hidden || onlyOnce || (order ?? 0) > 0;

const entryRules = (
  forms: readonly RuleForm[],
  allowEntryById: Readonly<Record<string, boolean>>,
  entries: readonly ListedEntry[],
  inCase: boolean
): Record<string, EntryRule> => {
  const firstEnforcing = forms.findIndex(form => form.enforcements.enforceOrder);
  const waitsForFirst = inCase && entries.length === 0 && firstEnforcing > -1;

  return Object.fromEntries(forms.map((form, index) => {
    const { enforcements, identifier } = form;
    const ruled = hasRules(enforcements);
    const hasEntry = ruled && entries.some(entry => entry.formIdentifier === identifier);
    const onlyOnce = enforcements.onlyOnce && hasEntry;
    const order = index > 0 && (enforcements.enforceOrder || (waitsForFirst && index > firstEnforcing));
    const condition = ruled && allowEntryById[identifier] === false;
    const reason: EntryRuleReason | undefined =
      onlyOnce ? 'onlyOnce' : order ? 'order' : condition ? 'condition' : undefined;
    return [identifier, { allowed: reason === undefined, reason, hasEntry, conditionMet: !condition }];
  }));
};

/**
 * Which of the study's forms one case may add an entry to, and why not, by form id.
 *
 * Pure, and it writes nothing: the menu used to get this by setting `enforceOrder` on the app-wide
 * `formList` itself, so one empty case's cascade stayed on the forms for every case after it.
 * `allowEntryById` is the per-case read's answer for this case; a form it has no answer for is
 * allowed, as the study's read without a case says.
 */
export const caseEntryRules = (
  forms: readonly RuleForm[],
  allowEntryById: Readonly<Record<string, boolean>>,
  caseEntries: readonly ListedEntry[]
): Record<string, EntryRule> => entryRules(forms, allowEntryById, caseEntries, true);

/**
 * The same, for a list with no case open: a study that is not longitudinal. Only a form's own
 * switches apply there. Nothing waits behind the first form that enforces order, since that wait is
 * about a case's first entry, and no effects are read, since `allowEntry` is computed per case.
 */
export const studyEntryRules = (
  forms: readonly RuleForm[],
  entries: readonly ListedEntry[]
): Record<string, EntryRule> => entryRules(forms, {}, entries, false);
